import React, { useState, useEffect, useMemo, useRef, useCallback, ReactElement, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { actions as locationDashboardActionsSource } from '@Actions/locationDashboardActions';
import { OverviewLocationFilters, LocationBreakdownRow } from '@Apis/companyDashboard';
import AssignTasksDrawer from '@Components/LocationDashboard/Widgets/AssignTasksDrawer';
import { LocationSummaryData } from '../types';
import LocationSummaryTile, { expandToggleId } from './Tiles/LocationSummaryTile';
import LocationBreakdownPanel, { BreakdownView, DEFAULT_BREAKDOWN_VIEW } from './LocationBreakdownPanel';
import { BreakdownSelection } from './breakdownSelection';
import './LocationsList.scss';

// Single source of truth for the tile height: published to CSS as --lst-tile-height and
// consumed by LocationSummaryTile.scss, so the virtualized row size and the rendered tile
// can never drift apart.
const TILE_HEIGHT = 78;
const ROW_GAP = 8;
const ROW_HEIGHT = TILE_HEIGHT + ROW_GAP;

// An expanded row draws the card border itself (see .overview-location-row--expanded in
// LocationsList.scss). The measured panel height is the panel content's offsetHeight only, so
// the virtualized row size has to reserve those two hairlines or the panel is clipped.
const EXPANDED_ROW_BORDER = 2;
const TILE_HEIGHT_VARS = { '--lst-tile-height': `${TILE_HEIGHT}px` } as React.CSSProperties;

// Must match the transition duration on .overview-location-row__panel in LocationsList.scss:
// the collapsing panel stays mounted for exactly this long so it has something to animate.
const PANEL_ANIMATION_MS = 220;

// Below this many locations the list renders inline so the page keeps a single scrollbar.
// Above it the list virtualizes into its own scroll area — a tab covering many projects
// can resolve to hundreds of locations, and every tile mounts a stacked-bar graph.
const VIRTUALIZE_THRESHOLD = 25;
const VIRTUAL_LIST_HEIGHT = 10 * ROW_HEIGHT;

interface LocationsListProps {
    locations: LocationSummaryData[];
    onLocationChange: (id: number) => void;
    /** A group inside an expanded breakdown panel was clicked. */
    onBreakdownGroupSelect: (selection: BreakdownSelection) => void;
    isLoadingMetrics?: boolean;
    /**
     * The page's active filters. They scope the breakdown panel's fetch, and their
     * `projectIds` are the selection every tile drill-down carries to the Tasks page.
     */
    filters: OverviewLocationFilters;
}

interface VirtualRowData {
    locations: LocationSummaryData[];
    renderRow: (location: LocationSummaryData) => ReactNode;
}

const VirtualRow = ({ data, index, style }: ListChildComponentProps<VirtualRowData>): ReactElement => (
    <div style={{ ...style, height: (style.height as number) - ROW_GAP }}>
        {data.renderRow(data.locations[index])}
    </div>
);

const LocationsList = ({
    locations,
    onLocationChange,
    onBreakdownGroupSelect,
    isLoadingMetrics,
    filters,
}: LocationsListProps): ReactElement => {

    const selectedProjectIds = filters.projectIds;

    const [activeLocationId, setActiveLocationId] = useState<number>(0);
    // Owned here rather than inside the tile: virtualized rows unmount when scrolled out
    // of the window, which would tear down an open drawer mid-edit.
    const [assignTasksLocationKey, setAssignTasksLocationKey] = useState<string | null>(null);

    const [expandedLocationId, setExpandedLocationId] = useState<number | null>(null);
    const [closingLocationId, setClosingLocationId] = useState<number | null>(null);
    const [breakdownView, setBreakdownView] = useState<BreakdownView>(DEFAULT_BREAKDOWN_VIEW);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dispatch = useDispatch();
    const locationDashboardActions = useMemo(
        () => bindActionCreators(locationDashboardActionsSource, dispatch),
        [dispatch]
    );

    const lastLocationIdsKey = useRef<string | null>(null);

    // This effect throws away the user's selection, so it has to fire when the *set* of
    // locations changes, not merely when the array is rebuilt. The overview-tab query refetches
    // on mount, on focus and on every filter tweak, and each response is a fresh array — without
    // this guard a background refetch that only moved a metric would collapse the open breakdown
    // panel, drop any drill-down and snap the selection back to the first row mid-read.
    useEffect(() => {
        const idsKey = locations.map((location) => location.id).sort((a, b) => a - b).join(',');
        if (lastLocationIdsKey.current === idsKey) return;
        lastLocationIdsKey.current = idsKey;

        setExpandedLocationId(null);
        setClosingLocationId(null);

        // Set the first location as active by default - if available
        if (locations.length > 0) {
            setActiveLocationId(locations[0].id);
            onLocationChange(locations[0].id);
            return;
        }
        setActiveLocationId(0);
        onLocationChange(0);
    }, [locations, onLocationChange]);

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    const onLocationClick = useCallback((id: number) => {
        setActiveLocationId(id);
        onLocationChange(id);
    }, [onLocationChange]);

    // Drilling into a group also makes its location the active one, so the sidebar the
    // breadcrumb points back to is the row the user is looking at.
    const onGroupSelect = useCallback((location: LocationSummaryData, row: LocationBreakdownRow) => {
        setActiveLocationId(location.id);
        onBreakdownGroupSelect({
            locationId: location.id,
            locationKey: location.locationKey,
            locationName: location.name,
            view: breakdownView,
            groupId: row.groupId,
            groupName: row.groupName,
        });
    }, [breakdownView, onBreakdownGroupSelect]);

    const [panelHeight, setPanelHeight] = useState(0);
    const [closingPanelHeight, setClosingPanelHeight] = useState(0);
    const latestPanelHeight = useRef(0);
    const panelResizeObserver = useRef<ResizeObserver | null>(null);

    const recordPanelHeight = useCallback((height: number) => {
        latestPanelHeight.current = height;
        setPanelHeight(height);
    }, []);

    const startClosing = useCallback((id: number) => {
        setClosingLocationId(id);
        setClosingPanelHeight(latestPanelHeight.current);
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setClosingLocationId(null), PANEL_ANIMATION_MS);
    }, []);

    const toggleExpanded = useCallback((id: number) => {
        const next = expandedLocationId === id ? null : id;
        if (expandedLocationId !== null && expandedLocationId !== next) {
            startClosing(expandedLocationId);
        }
        setExpandedLocationId(next);
    }, [expandedLocationId, startClosing]);

    const collapse = useCallback(() => {
        if (expandedLocationId === null) return;
        startClosing(expandedLocationId);
        setExpandedLocationId(null);
        document.getElementById(expandToggleId(expandedLocationId))?.focus();
    }, [expandedLocationId, startClosing]);

    const measurePanelRef = useCallback((node: HTMLDivElement | null) => {
        panelResizeObserver.current?.disconnect();
        panelResizeObserver.current = null;

        if (!node) return;

        recordPanelHeight(node.offsetHeight);

        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => recordPanelHeight(node.offsetHeight));
        observer.observe(node);
        panelResizeObserver.current = observer;
    }, [recordPanelHeight]);

    useEffect(() => () => panelResizeObserver.current?.disconnect(), []);

    useEffect(() => {
        if (expandedLocationId !== null || closingLocationId !== null) return;
        recordPanelHeight(0);
        setClosingPanelHeight(0);
    }, [expandedLocationId, closingLocationId, recordPanelHeight]);

    const closeAssignTasks = () => setAssignTasksLocationKey(null);

    const isVirtualized = locations.length > VIRTUALIZE_THRESHOLD;

    const renderRow = useCallback((location: LocationSummaryData) => {
        const isExpanded = expandedLocationId === location.id;
        const isClosing = closingLocationId === location.id;
        const isActive = activeLocationId === location.id;

        // The expanded row draws the selection border in place of the tile, so it needs the
        // selected state as a class of its own — see the note in LocationsList.scss.
        const rowClassName = [
            'overview-location-row',
            isExpanded ? 'overview-location-row--expanded' : '',
            isActive ? 'overview-location-row--active' : '',
        ].filter(Boolean).join(' ');

        return (
            <div className={rowClassName}>
                <LocationSummaryTile
                    location={location}
                    onClick={() => onLocationClick(location.id)}
                    isActive={isActive}
                    isLoadingMetrics={isLoadingMetrics}
                    onAssignTasks={() => setAssignTasksLocationKey(location.locationKey)}
                    selectedProjectIds={selectedProjectIds}
                    isExpanded={isExpanded}
                    onToggleExpand={() => toggleExpanded(location.id)}
                />
                <div className='overview-location-row__panel'>
                    <div
                        className='overview-location-row__panel-content'
                        ref={isVirtualized && isExpanded ? measurePanelRef : undefined}>
                        {(isExpanded || isClosing) && (
                            <LocationBreakdownPanel
                                locationName={location.name}
                                locationKey={location.locationKey}
                                filters={filters}
                                view={breakdownView}
                                onViewChange={setBreakdownView}
                                onClose={collapse}
                                isExpanded={isExpanded}
                                onGroupSelect={(row) => onGroupSelect(location, row)}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }, [
        expandedLocationId, closingLocationId, breakdownView, activeLocationId, isVirtualized,
        isLoadingMetrics, selectedProjectIds, filters, onLocationClick, onGroupSelect, toggleExpanded,
        collapse, measurePanelRef,
    ]);

    const assignTasksDrawer = assignTasksLocationKey !== null && (
        <AssignTasksDrawer
            locationKey={assignTasksLocationKey}
            locationDashboardActions={locationDashboardActions}
            onComplete={closeAssignTasks}
            onCancel={closeAssignTasks}
        />
    );

    const listRef = useRef<List>(null);
    const virtualItemData = useMemo<VirtualRowData>(() => ({ locations, renderRow }), [locations, renderRow]);

    const getItemSize = useCallback(
        (index: number) => {
            const id = locations[index]?.id;
            if (id === undefined) return ROW_HEIGHT;
            if (id === expandedLocationId) return ROW_HEIGHT + panelHeight + EXPANDED_ROW_BORDER;
            if (id === closingLocationId) return ROW_HEIGHT + closingPanelHeight;
            return ROW_HEIGHT;
        },
        [locations, expandedLocationId, closingLocationId, panelHeight, closingPanelHeight]
    );

    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [getItemSize]);

    if (isVirtualized) {
        return (
            <div className='overview-locations-list overview-locations-list--virtual' style={TILE_HEIGHT_VARS}>
                <List
                    ref={listRef}
                    height={Math.min(locations.length * ROW_HEIGHT, VIRTUAL_LIST_HEIGHT)}
                    itemCount={locations.length}
                    itemSize={getItemSize}
                    estimatedItemSize={ROW_HEIGHT}
                    width={'100%'}
                    itemData={virtualItemData}
                    itemKey={(index, data) => data.locations[index].id}>
                    {VirtualRow}
                </List>
                {assignTasksDrawer}
            </div>
        );
    }

    return (
        <div className='overview-locations-list' style={TILE_HEIGHT_VARS}>
            {locations.map((location) => (
                <React.Fragment key={location.id}>{renderRow(location)}</React.Fragment>
            ))}
            {assignTasksDrawer}
        </div>
    );
};

export default LocationsList;
